import React from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Table, Col, Card, CardBody, Spinner, CardFooter } from 'reactstrap';
import { LEAVE_STATUS_BADGE_STYLES, wfhHeaders } from './constants';
import { fetchWfhHistory } from '../../../store/apis/wfhService';
import { useTableHook } from '../../../components/hooks';
import { fetchEmployeeLeavesColumn } from '../../../store/apis/leaveService';
import {
  FilterDropdown,
  TableFilterBubbles,
  TableSortDisplayArrow,
  Badge,
  DatePicker,
  TablePagination,
} from '../../../components/Common';
import { Messages } from '../../../common/messages';

function WFHEnquiry() {
  document.title = 'WFH Enquiry | Envision Daikin';
  const navigate = useNavigate();
  // Table Hook
  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    isLoadingData,
    isLoadingColumn,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'RequestID',
      sortDirection: 'DESC',
      filters: [],
    },
    fetchFn: fetchWfhHistory,
    fetchColumnFn: fetchEmployeeLeavesColumn,
    queryKeyPrefix: 'wfhEnquiry',
    columnKeyPrefix: 'wfhEnquiryColumn',
    tableName: 'WFHEnquiry',
  });

  const applyExternalFilters = () => {
    setPageRequest({
      ...pageRequest,
      ...externalFilters,
    });
  };

  const handleLinkToView = item => {
    navigate(`/wfh/apply?ID=${item?.RequestID}`);
  };

  const renderTableBody = () => {
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={6}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_WFH
            )}
          </td>
        </tr>
      );
    }

    return tableData.map(item => (
      <tr
        key={item.RequestID}
        onClick={() => {
          handleLinkToView(item);
        }}
        className="cursor-pointer"
      >
        <td className="cursor-pointer text-decoration-underline text-primary text-header">
          {item.RequestID}
        </td>
        <td>{item.RequestDate}</td>
        <td>{item['From Date'] || '-'}</td>
        <td>{item['To Date'] || '-'}</td>
        <td>
          <span>{item.Approver}</span>
        </td>
        <td>
          <Badge
            config={LEAVE_STATUS_BADGE_STYLES[item.Status]}
            labelType="TitleCase"
            badgeType="pill"
            dot
          >
            {item.Status}
          </Badge>
        </td>
      </tr>
    ));
  };

  const externalFiltersValidation = () => {
    if (
      externalFilters.leaveFromDate &&
      externalFilters.leaveToDate &&
      new Date(externalFilters?.leaveFromDate) >
        new Date(externalFilters?.leaveToDate)
    ) {
      toast.error('To Date should be greater than equal to From Date.', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  return (
    <div className="page-content main-table-page">
      <Card className="enquiry-card">
        <CardBody className="p-0">
          <div className="p-3">
            <span className="fw-semibold fs-5">WFH Enquiry</span>
          </div>
          <div className="external-filter gap-2 mx-3 p-3">
            <Col className="external-filter-col">
              <b>WFH From Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    leaveFromDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.leaveFromDate || ''}
              />
            </Col>
            <Col className="external-filter-col">
              <b>WFH To Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    leaveToDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.leaveToDate || ''}
              />
            </Col>
            <Col className="d-flex align-items-center justify-content-end">
              <div className="external-filter-btns gap-2">
                <button
                  type="button"
                  className="custom-btn"
                  onClick={() => {
                    if (!externalFiltersValidation()) {
                      return;
                    }
                    applyExternalFilters(externalFilters);
                  }}
                >
                  Search
                </button>
              </div>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={wfhHeaders}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive">
            <Table hover className="custom-table align-middle text-nowrap">
              <thead>
                <tr>
                  {Object.entries(wfhHeaders).map(([key, value]) => (
                    <th key={key}>
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => {
                            handleAutoSort(key);
                          }}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
    </div>
  );
}

export default WFHEnquiry;
